export const oohData = {
  headlineVerdict: 'Premium branding, risky in fast traffic',
  reportMeta: {
    campaign: 'Naturalmente Equilibrada',
    creativeVersion: 'Landscape large-format, v1',
    market: 'Portugal, urban',
    preparedBy: 'Brandpulse Attention Blueprint',
  },
  methodology: {
    name: 'Attention Blueprint',
    summary:
      'Our methodology is built on 17 visual drivers from cognitive psychology that determine what wins that first half-second of attention. Every principle is substantiated with peer-reviewed research.',
    source: 'University of Utrecht · Cognitive Psychology Research',
    stats: ['17 drivers', '47+ peer-reviewed sources'],
    driverCount: 17,
    // This report observes the creative itself. It does not measure viewers.
    observationNote:
      'This report is an observational analysis. It reads the creative against research-backed attention drivers to establish where attention is likely to land, before a single euro of media is spent.',
  },
  attentionHeatmap: {
    title: 'Attention Heatmap',
    subtitle:
      'Predicted attention: where the eye is drawn in the first seconds of exposure.',
    // Leads with the strength of the model rather than apologising for not
    // being live eye-tracking. No third-party benchmark bodies are named.
    credibility:
      'AI trained on 5.5 million+ real eye-tracking fixations. The model predicts where people look with up to 96% accuracy against real eye-tracking studies, and maps the first seconds of viewing, which is precisely the window that decides an OOH execution.',
    modelStats: [
      '5.5M+ eye-tracking fixations',
      'Up to 96% accuracy',
      'First seconds of viewing',
    ],
    images: {
      clean:
        'https://cdn.magicpatterns.com/uploads/kaRcvsRReWsHiSpwCoP3cb/heatmap_Luso.png',
      aoi: 'https://cdn.magicpatterns.com/uploads/7TQAoMAPA38tLXYypDxMHA/heatmap_aoi_Luso.png',
    },
    predictedScore: {
      value: 68,
      max: 100,
      label: 'Moderate',
      // Claims only what the score can support: attention capture, not reading.
      caption:
        'The Brandpulse Attention Score for this execution. It reflects how strongly the creative captures and directs attention. It does not assess how easily the message can be read once the eye arrives, which is what the driver analysis below examines.',
    },
    // Objective measurements against category benchmarks.
    aois: [
      {
        label: 'Main Message',
        value: 83.6,
        benchmark: 35.3,
        verdict: 'high' as const,
        note: 'Takes the overwhelming majority of attention, far above benchmark. Read this alongside the legibility note below before treating it as a win.',
      },
      {
        label: 'Product Visual',
        value: 23.2,
        benchmark: 29.3,
        verdict: 'low' as const,
        note: 'The bottle draws less attention than a typical product execution, despite being sharply focused and centrally placed.',
      },
      {
        label: 'Logo',
        value: 8.0,
        benchmark: 9.5,
        verdict: 'low' as const,
        note: 'The Luso mark barely registers, which is a direct brand linkage risk.',
      },
    ],
    // Ties the measurements back to the stated Branding objective.
    objectiveRead:
      'Against a Branding objective, this distribution is the core issue. The two elements that carry brand recognition, the logo at 8.0% and the product at 23.2%, both sit below benchmark, while the headline absorbs the attention. The creative is winning attention for the line, not for the brand.',
    // The most important nuance in the report. Deliberately makes no claim
    // about comprehension, only about how hard the words are to resolve.
    nuance: {
      heading: 'Attention is not the same as legibility',
      body: 'The headline takes 83.6% of predicted attention, well above the 35.3% benchmark, so the creative is clearly pulling the eye to the message. What the heatmap cannot show is whether the words can be resolved once the eye arrives. Because the type sits behind a refractive glass bottle, several letterforms are broken and distorted, which raises the effort needed to read them. A high attention share on type that is hard to resolve is not the same as a message delivered.',
    },
  },
  attentionTesting: {
    eyebrow: 'Recommended next step',
    heading: 'Pressure-test this creative on real human behaviour',
    body: 'This report establishes where attention lands and which visual drivers put it there. Attention Testing answers the next question: what real people actually do with the creative, and which version performs best when several are put in front of them.',
    bullets: [
      'Compare multiple variants head-to-head (A / B / C / D and beyond)',
      'Measure real viewer behaviour rather than predicted attention',
      'Confirm the fixes in this report actually move the needle',
    ],
    ctaLabel: 'Explore Attention Testing',
    ctaHref: 'https://brandpulse-analytics.com/attention-testing',
  },
  driverDefinitions: {
    SIZE: 'How large and visually dominant key elements are. Bigger elements get seen first, especially in a fleeting glance.',
    COLOUR:
      'Whether standout hues are present. Vivid, saturated colour draws the eye pre-attentively, before conscious reading begins.',
    'COLOUR CONTRAST':
      'The difference in hue between an element and its surroundings. High hue contrast makes elements pop from the background.',
    'LUMINANCE CONTRAST':
      'The brightness difference between an element and its background. This is the single strongest low-level driver of where the eye lands.',
    'TEXTURE CONTRAST':
      'How much surface detail differs across the frame. Busy areas set against smooth ones guide fixations toward the focal point.',
    'GENERAL CONTRAST':
      'Overall figure-ground separation, meaning how cleanly the subject stands apart from everything behind it.',
    'ORIENTATION & EDGES':
      'The angles and edge structure of elements. Strong, clean edges anchor attention, while clashing edges disrupt legibility.',
    'CURVATURE CONTRAST':
      'Whether curved shapes stand out against straight ones. Curvature can create pre-attentive pop-out.',
    FACES:
      'Presence of human faces. The brain has dedicated circuitry that captures attention on faces almost immediately.',
    'EYE GAZE':
      'Whether depicted subjects make or direct eye contact. Gaze powerfully steers a viewer’s own attention.',
    'EMOTIONAL EXPRESSIONS':
      'Visible emotion in the creative. Emotional cues are processed quickly and support memorability.',
    'CUTENESS / KINDCHENSCHEMA':
      'Baby-schema features such as infants or young animals, which trigger an automatic, hard-wired attention response.',
    'SPATIAL PROXIMITY / DEPTH':
      'How near the subject feels to the viewer. Objects in the near-space read as more present and more premium.',
    'GESTALT PRINCIPLES':
      'How the brain groups and completes shapes. Good grouping lets forms be read at a glance, while broken grouping raises the effort required.',
    TEXT: 'Legibility and prominence of the message. Type needs to be resolvable within the exposure window the placement allows.',
  } as Record<string, string>,
  brandContext: {
    brandName: 'Água de Luso',
    brandWebsite: 'https://www.aguadeluso.pt/pt/',
    industry: 'FMCG / Beverages',
    targetAudience:
      'General public, health-conscious consumers, individuals seeking natural hydration',
    brandTone: 'Natural, pure, balanced, serene, historical',
    marketSegment: 'Large National Brand',
    creativeSummary:
      "Glass bottle of Água de Luso and a branded drinking glass resting on a mossy surface in a sunlit forest setting. Large white serif text reading 'Naturalmente Equilibrada' spans the centre, partially obscured by the bottle and glass.",
    oohObjective: 'Branding',
    objectiveRationale:
      'The creative is entirely focused on brand building, product purity, and natural origins. No direct call to action, QR code, promotional offer, or conversion mechanism present.',
    imageUrl:
      'https://cdn.magicpatterns.com/uploads/do9hxtQbnJMwAsBYtxynrJ/5b9992d5-0699-4975-a16c-d1288f8c9076.jpg',
  },
  viewingContext: {
    description:
      'Landscape format, likely deployed across large-format billboards or transit shelters in urban environments. Viewing mode spans from fast (drivers and cyclists at 2 to 6 seconds) to medium (pedestrians at 5 to 10 seconds). As a working rule of thumb for large-format OOH, roughly 2 to 3 seconds of usable exposure is needed for a message of this length to resolve.',
    modes: [
      {
        label: 'Fast',
        time: '2-6s',
        verdict: 'weak' as const,
        note: 'The headline fragments behind the glass, so the line may not resolve before the viewer has passed.',
      },
      {
        label: 'Medium',
        time: '5-10s',
        verdict: 'moderate' as const,
        note: 'Enough time to resolve the wordmark, though the logo still registers late.',
      },
      {
        label: 'Dwell',
        time: '10s+',
        verdict: 'strong' as const,
        note: 'The craft, depth and premium mood land fully at this exposure.',
      },
    ],
  },
  drivers: [
    {
      id: '01',
      name: 'SIZE',
      verdict: 'strong_with_improvement',
      observation:
        "The headline 'Naturalmente Equilibrada' is massive and dominates the horizontal space, and the product bottle is large and centrally placed. The primary Luso logo in the top left is relatively small against the visual weight of the headline.",
      improvement:
        'Increase the top-left Luso logo by roughly 25 to 50%, with more clear space around it, so the brand registers earlier in a short exposure.',
      category: 'Visual Capture',
    },
    {
      id: '02',
      name: 'COLOUR',
      verdict: 'neutral_with_improvement',
      observation:
        "Palette dominated by natural desaturated greens and earthy browns. The blue cap and red 'LUSO' on the bottle label provide the only chromatic pop, and both are relatively small.",
      improvement:
        'Lift the saturation of the blue cap and the red logo on the label so they carry more bottom-up capture against the green and brown palette.',
      category: 'Visual Capture',
    },
    {
      id: '03',
      name: 'COLOUR CONTRAST',
      verdict: 'strong_no_improvement',
      observation:
        "Strong hue contrast between stark white typography and the dark green and brown forest background. Red 'LUSO' text on the bottle contrasts effectively with the surrounding cool tones.",
      improvement: null,
      category: 'Visual Capture',
    },
    {
      id: '04',
      name: 'LUMINANCE CONTRAST',
      verdict: 'strong_no_improvement',
      observation:
        'Bright white text and specular highlights on the glass bottle create extreme luminance contrast against the deeply shadowed forest background, pulling the eye to the centre.',
      improvement: null,
      category: 'Visual Capture',
    },
    {
      id: '05',
      name: 'TEXTURE CONTRAST',
      verdict: 'strong_no_improvement',
      observation:
        'High-frequency mossy textures and bottle reflections contrast sharply with the smooth blurred background, guiding fixations to the product.',
      improvement: null,
      category: 'Visual Capture',
    },
    {
      id: '06',
      name: 'GENERAL CONTRAST',
      verdict: 'strong_no_improvement',
      observation:
        'Shallow depth of field isolates the bottle, glass and text from the blurred background, giving excellent figure-ground separation.',
      improvement: null,
      category: 'Visual Capture',
    },
    {
      id: '07',
      name: 'ORIENTATION & EDGES',
      verdict: 'neutral_with_improvement',
      observation:
        'Strong vertical bottle edges intersect the horizontal typography, creating a central cross-axis. The text overlapping the bottle also creates intersecting edges that disrupt legibility.',
      improvement:
        'Adjust kerning or placement so the vertical edges of the bottle do not cut through the middle of individual letters.',
      category: 'Shape & Edge',
    },
    {
      id: '08',
      name: 'CURVATURE CONTRAST',
      verdict: 'neutral_not_applicable',
      observation:
        'Curved bottle and rounded serif typography stand out against the rectilinear frame, but no highly disruptive curvature elements are deployed specifically for pre-attentive pop-out.',
      improvement: null,
      category: 'Shape & Edge',
    },
    {
      id: '09',
      name: 'FACES',
      verdict: 'neutral_not_applicable',
      observation: 'No human faces present in the execution.',
      improvement: null,
      category: 'Biological Attention',
    },
    {
      id: '10',
      name: 'EYE GAZE',
      verdict: 'neutral_not_applicable',
      observation: 'No subjects with eyes depicted.',
      improvement: null,
      category: 'Biological Attention',
    },
    {
      id: '11',
      name: 'EMOTIONAL EXPRESSIONS',
      verdict: 'neutral_not_applicable',
      observation: 'No emotional expressions depicted.',
      improvement: null,
      category: 'Biological Attention',
    },
    {
      id: '12',
      name: 'CUTENESS / KINDCHENSCHEMA',
      verdict: 'neutral_not_applicable',
      observation: 'No infants, young animals, or baby-schema features present.',
      improvement: null,
      category: 'Biological Attention',
    },
    {
      id: '13',
      name: 'SPATIAL PROXIMITY / DEPTH',
      verdict: 'strong_no_improvement',
      observation:
        "Shallow depth of field pushes the bottle into the viewer's near-space while the blurred forest recedes, creating a strong sense of physical proximity to the product.",
      improvement: null,
      category: 'Structural & Cognitive',
    },
    {
      id: '14',
      name: 'GESTALT PRINCIPLES',
      verdict: 'weak_with_improvement',
      observation:
        "The bottle obscures parts of 'Naturalmente' and 'Equilibrada'. This is visually sophisticated, but it leans on the brain completing the words, which raises the effort required in fast-moving OOH contexts.",
      improvement:
        'Reduce the reliance on closure by shifting the bottle off the vertical centre of the headline, or by lifting the type clear of the glass, so the words read as complete shapes at a glance.',
      category: 'Structural & Cognitive',
    },
    {
      id: '15',
      name: 'TEXT',
      verdict: 'weak_with_improvement',
      observation:
        'Serif typography is elegant and large, but legibility is materially compromised by sitting behind the refractive glass bottle, which fragments the line in a fast-viewing context.',
      improvement:
        'Reduce the overlap between type and glass so no letterform is broken at its key strokes. In order of least disruption to the aesthetic: shift the bottle off the vertical centre of the headline, increase the weight or size of the type where it crosses the glass, or bring the type fully to the foreground.',
      category: 'Structural & Cognitive',
    },
  ],
  systemDynamics: {
    // How the individual drivers combine. Shown as the section intro so the
    // risk cards below have context.
    interactionSummary:
      'Individual attention drivers do not work in isolation. The visual hierarchy pulls the eye straight to the centre of the canvas, with extreme luminance contrast, shallow depth of field and spatial proximity all reinforcing the product. The catch is that the massive headline and the refractive bottle collide in that same centre, so the eye has to untangle text from product rather than absorbing them in one clean sweep. This is where a strength in one area quietly creates a risk in another.',
    risks: [
      {
        label: 'Brand Linkage Risk',
        definition: 'Will people connect what they see to your brand?',
        level: 'moderate' as const,
        reason:
          'The Luso logo is small and sits apart from the bright central cluster, so the eye locks onto a beautiful forest scene before it registers whose ad it is.',
        evidence:
          'Heatmap confirms it: the logo draws just 8.0% of attention, below the 9.5% category benchmark.',
        mitigation: 'Addressed by the SIZE fix.',
      },
      {
        label: 'Attention Hierarchy Risk',
        definition: 'The order the eye travels through the creative.',
        level: 'high' as const,
        reason:
          'The headline and the refractive bottle collide in the centre, forcing the eye to untangle text from product instead of absorbing them in one clean sweep.',
        evidence:
          'Heatmap confirms it: the message takes 83.6% of attention against a 35.3% benchmark, overpowering both product at 23.2% and logo at 8.0%.',
        mitigation: 'Addressed by the TEXT and SIZE fixes.',
      },
      {
        label: 'Context Vulnerability',
        definition:
          'How well the message survives real-world viewing conditions.',
        level: 'high' as const,
        // Nuanced: the risk is placement-dependent, not absolute.
        reason:
          'This risk is placement-dependent. At dwell placements the execution performs well. In fast traffic the broken letterforms mean a viewer can take in the scene without the line resolving.',
        evidence:
          'Heatmap confirms it: attention smears across the headline band instead of resolving to one sharp anchor point.',
        mitigation: 'Addressed by the TEXT and GESTALT fixes.',
      },
    ],
  },
  overallAssessment:
    'This execution is a visually accomplished, premium piece of branding. It excels at creating a mood of natural purity through depth, luminance contrast and figure-ground separation, and those strengths are real and worth protecting. Its vulnerability is mechanical rather than aesthetic: the core message sits behind a refractive glass bottle, which breaks several letterforms and raises the effort needed to read the line at a glance. At dwell placements that sophistication reads as craft. In fast traffic it becomes a legibility barrier, and the brand mark is too small and too far from the centre to compensate. The most valuable change is to reduce the overlap between type and glass while keeping the depth and contrast that make the image work.',
  // Short form for the Executive Summary at the top of the report.
  // The fuller rationale lives in the Key Takeaways section at the bottom.
  topStrengths: [
    {
      name: 'LUMINANCE CONTRAST',
      summary:
        'Bright white type against the dark forest anchors the eye to the centre immediately.',
      detail:
        'The brightness difference between the white type, the specular highlights on the glass and the deeply shadowed forest is the strongest single reason this creative captures attention. Protect this relationship in any revision, because lifting the background brightness or softening the highlights will cost attention.',
    },
    {
      name: 'GENERAL CONTRAST',
      summary:
        'Shallow depth of field separates the product and message from background noise.',
      detail:
        'The out-of-focus forest gives clean figure-ground separation, which lets the execution read as a single clear subject rather than a busy scene. In cluttered urban environments that is a genuine asset and should be retained through any layout change.',
    },
    {
      name: 'SPATIAL PROXIMITY / DEPTH',
      summary:
        "The sharply focused bottle sits in the viewer's near-space and reads as premium.",
      detail:
        'Pushing the bottle into the near-space is doing the premium positioning work here. It is also why the product still holds attention despite scoring below benchmark, and it can be preserved even if the bottle shifts off centre to solve the legibility issue.',
    },
  ],
  topImprovements: [
    {
      name: 'TEXT',
      summary:
        'Reduce the overlap between type and glass so words are not broken at their key strokes.',
      detail:
        'In order of least disruption to the aesthetic: shift the bottle off the vertical centre of the headline, increase the weight or size of the type where it crosses the glass, or bring the type fully to the foreground. Any of these should materially improve first-glance legibility in fast-traffic placements.',
    },
    {
      name: 'GESTALT PRINCIPLES',
      summary:
        'Lower the reliance on the brain completing partially hidden words.',
      detail:
        'The overlap currently asks the viewer to reconstruct two long words from fragments. Shifting the bottle slightly off the headline axis, or lifting the type clear of the glass, keeps the layered composition while letting the words read as complete shapes.',
    },
    {
      name: 'SIZE',
      summary:
        'Increase the Luso logo by roughly 25 to 50% and give it more clear space.',
      detail:
        'At 8.0% attention the mark is below benchmark and disconnected from the high-contrast centre. A larger logo with more clear space, or one positioned closer to the central cluster, should bring brand recognition forward in short exposures.',
    },
  ],
}
